import { createContext, useContext, useState, useCallback } from 'react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { CloseButton } from '@/components/ui';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

const NotificationItem = ({ notification, onRemove }) => {
  const getIcon = () => {
    switch (notification.type) {
      case 'success':
        return <CheckCircleIcon className="w-5 h-5 text-success" />;
      case 'warning':
        return <ExclamationTriangleIcon className="w-5 h-5 text-warning" />;
      case 'error':
        return <XCircleIcon className="w-5 h-5 text-error" />;
      default:
        return <InformationCircleIcon className="w-5 h-5 text-info" />;
    }
  };

  const getAlertClass = () => {
    switch (notification.type) {
      case 'success':
        return 'alert-success';
      case 'warning':
        return 'alert-warning';
      case 'error':
        return 'alert-error';
      default:
        return 'alert-info';
    }
  };

  return (
    <div className={`alert ${getAlertClass()} shadow-lg animate-fade-in-up`}>
      <div className="flex items-start gap-3 w-full">
        {getIcon()}
        <div className="flex-1">
          <h4 className="font-medium">{notification.title}</h4>
          {notification.message && (
            <p className="text-sm opacity-90 mt-1">{notification.message}</p>
          )}
          {notification.details && (
            <p className="text-xs opacity-75 mt-1 font-mono">{notification.details}</p>
          )}
        </div>
        <CloseButton onClick={() => onRemove(notification.id)} size="xs" />
      </div>
    </div>
  );
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  const addNotification = useCallback((notification) => {
    const id = Date.now() + Math.random();
    const newNotification = {
      id,
      type: 'info',
      autoRemove: true,
      duration: 5000,
      ...notification
    };

    setNotifications(prev => [...prev, newNotification]);

    // Auto-remove after duration
    if (newNotification.autoRemove) {
      setTimeout(() => {
        removeNotification(id);
      }, newNotification.duration);
    }

    return id;
  }, []);

  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  const showSuccess = useCallback((title, message, duration = 5000, details) => {
    return addNotification({
      type: 'success',
      title,
      message,
      details,
      duration
    });
  }, [addNotification]);

  const showError = useCallback((title, message, duration = 8000, details) => {
    return addNotification({
      type: 'error',
      title,
      message,
      details,
      duration
    });
  }, [addNotification]);

  const showWarning = useCallback((title, message, duration = 6000, details) => {
    return addNotification({
      type: 'warning',
      title,
      message,
      details,
      duration
    });
  }, [addNotification]);

  const showInfo = useCallback((title, message, duration = 5000, details) => {
    return addNotification({
      type: 'info',
      title,
      message,
      details,
      duration
    });
  }, [addNotification]);

  const value = {
    notifications,
    addNotification,
    removeNotification,
    clearAllNotifications,
    showSuccess,
    showError,
    showWarning,
    showInfo
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      
      {/* Notification Container - only render when there are notifications */}
      {notifications.length > 0 && (
        <div className="fixed top-16 right-4 left-4 sm:left-auto z-50 space-y-2 sm:max-w-sm">
          {notifications.map(notification => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onRemove={removeNotification}
            />
          ))}
        </div>
      )}

      {/* Add CSS for animations */}
      <style>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fade-in-up {
          animation: fade-in-up 0.3s ease-out;
        }
        
        .alert {
          transition: all 0.2s ease;
        }
        
        .alert:hover {
          transform: translateY(-1px);
          box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
        }
      `}</style>
    </NotificationContext.Provider>
  );
};

// Utility hook for quick notifications
export const useQuickNotify = () => {
  const { showSuccess, showError, showWarning, showInfo } = useNotifications();
  
  return {
    success: showSuccess,
    error: showError,
    warning: showWarning,
    info: showInfo
  };
};

export default NotificationProvider;